import { useState, useRef, useCallback } from 'react';
import { DEFAULT_SCALE, DEFAULT_HORIZONTAL_SCALE } from '../utils/constants';
import { serializeProject, deserializeProject } from '../utils/projectSerializer';
import { exportSvgToPdf } from '../utils/pdfExport';

const DEFAULT_BOREHOLES = [
  {
    id: 1,
    name: 'B',
    depth: 217.23,
    distance: 0,
    x: 25.5,
    y: 40.2,
    waterLevels: [],
    layers: [
      { id: 1, name: 'Bc', thickness: 2, color: '#8B4513', pattern: 'none', soilState: '', geotechnicalLayer: '' },
      { id: 2, name: 'Gr', thickness: 5, color: '#FFD700', pattern: 'none', soilState: '', geotechnicalLayer: '' },
      { id: 3, name: 'Π', thickness: 3, color: '#4682B4', pattern: 'none', soilState: '', geotechnicalLayer: '' },
      { id: 4, name: 'Po(+G)', thickness: 5, color: '#D2691E', pattern: 'none', soilState: '', geotechnicalLayer: '' }
    ]
  },
  {
    id: 2,
    name: 'Otwór 2',
    depth: 220.00,
    distance: 150,
    x: 65.8,
    y: 72.3,
    waterLevels: [],
    layers: [
      { id: 1, name: 'Bc', thickness: 1, color: '#8B4513', pattern: 'none', soilState: '', geotechnicalLayer: '' },
      { id: 2, name: 'Gr', thickness: 2, color: '#FFD700', pattern: 'none', soilState: '', geotechnicalLayer: '' },
      { id: 3, name: 'Π', thickness: 10, color: '#4682B4', pattern: 'none', soilState: '', geotechnicalLayer: '' },
      { id: 4, name: 'Po(+G)', thickness: 2, color: '#D2691E', pattern: 'none', soilState: '', geotechnicalLayer: '' }
    ]
  },
  {
    id: 3,
    name: 'Otwór 3',
    depth: 218.50,
    distance: 300,
    x: 88.1,
    y: 15.7,
    waterLevels: [],
    layers: [
      { id: 1, name: 'Bc', thickness: 1.5, color: '#8B4513', pattern: 'none', soilState: '', geotechnicalLayer: '' },
      { id: 2, name: 'Gr', thickness: 3, color: '#FFD700', pattern: 'none', soilState: '', geotechnicalLayer: '' },
      { id: 3, name: 'Π', thickness: 6, color: '#4682B4', pattern: 'none', soilState: '', geotechnicalLayer: '' },
      { id: 4, name: 'Po(+G)', thickness: 4, color: '#D2691E', pattern: 'none', soilState: '', geotechnicalLayer: '' }
    ]
  }
];

/**
 * Hook zarządzający core data projektu: otwory, rysunki, soczewki, skale.
 * Single source of truth dla danych persystowanych.
 */
export function useProjectData(initialData) {
  const [boreholes, setBoreholes] = useState(
    initialData?.boreholes || DEFAULT_BOREHOLES
  );
  const [drawings, setDrawings] = useState(initialData?.drawings || []);
  const [lenses, setLenses] = useState(initialData?.lenses || []);
  const [scale, setScale] = useState(
    initialData?.settings?.scale || DEFAULT_SCALE
  );
  const [horizontalScale, setHorizontalScale] = useState(
    initialData?.settings?.horizontalScale || DEFAULT_HORIZONTAL_SCALE
  );
  const fileInputRef = useRef(null);

  /**
   * Eksportuje cały projekt do pliku JSON.
   * @param {Object} extraState - dodatkowe stany z innych hooków (settings, connections, drawingSettings)
   */
  const exportData = (extraState) => {
    const defaultFileName = 'przekroj_geologiczny';
    const fileName = prompt('Podaj nazwę pliku (bez rozszerzenia .json):', defaultFileName);
    if (fileName === null || fileName.trim() === '') return;

    const cleanFileName = fileName.trim().replace(/\.json$/i, '');

    const fullData = serializeProject({
      boreholes,
      scale,
      horizontalScale,
      drawings,
      lenses,
      ...extraState,
    });

    const dataStr = JSON.stringify(fullData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${cleanFileName}.json`;
    link.click();
  };

  /**
   * Importuje projekt z pliku JSON.
   * @param {Event} event - event z input[type=file]
   * @param {Function} onImport - callback z zdeserializowanymi danymi do ustawienia w innych hookach
   */
  const importData = (event, onImport) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        const data = deserializeProject(jsonData);

        if (!data) {
          alert('Nieprawidłowy format pliku JSON');
          return;
        }

        // Ustaw core data
        setBoreholes(data.boreholes);
        setScale(data.settings.scale);
        setHorizontalScale(data.settings.horizontalScale);
        setDrawings(data.drawings);
        setLenses(data.lenses);

        // Przekaż resztę do orkiestratora
        if (onImport) {
          onImport(data);
        }

        if (data.isLegacyFormat) {
          alert('Otwory zostały wczytane pomyślnie! (stary format - tylko otwory)');
        } else {
          alert('Przekrój został wczytany pomyślnie!');
        }
      } catch (error) {
        console.error('Błąd podczas wczytywania pliku:', error);
        alert('Błąd podczas wczytywania pliku JSON: ' + error.message);
      }
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  const exportToPDF = useCallback(async (pdfOptions = {}) => {
    const svgElement = document.getElementById('geological-profile-svg');
    if (!svgElement) {
      alert('Nie znaleziono przekroju geologicznego do eksportu.');
      return;
    }
    try {
      await exportSvgToPdf(svgElement, pdfOptions);
    } catch (err) {
      console.error('Błąd eksportu PDF:', err);
      alert('Błąd podczas eksportu do PDF: ' + err.message);
    }
  }, []);

  return {
    boreholes, setBoreholes,
    drawings, setDrawings,
    lenses, setLenses,
    scale, setScale,
    horizontalScale, setHorizontalScale,
    fileInputRef,
    exportData,
    importData,
    exportToPDF,
  };
}
